import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { DashboardDef, DashboardFilterDef, HealthRow, Rag, WidgetDef } from '@project/contracts'
import { Button, Icon, Lookup, Money, Panel } from '../design/index.js'
import { useApi, type DashboardResult, type WidgetData } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths, type Query } from '../app/router.js'
import { ErrorBlock, Loading } from './feedback.js'
import { formatQuantity } from './format.js'
import { answersRequired, defaultFilterValues } from './filters.js'
import { DateRangePicker } from './DateRangePicker.js'
import { StarToggle } from './favourites.js'
import { RatioBand, Ring, SeriesChart, SplitRing, colourAt, formatFigure, type ChartType } from './charts.js'

const useDashboardDefs = () => {
  const api = useApi()
  return useQuery({ queryKey: ['dashboardDefs'], queryFn: () => api.dashboardMenu(), staleTime: 10 * 60_000 })
}

export const DashboardsHub = ({ companyId }: { companyId: string }) => {
  const defs = useDashboardDefs()
  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (defs.error) return <div className="page"><ErrorBlock error={defs.error} /></div>
  return (
    <div className="page">
      <h1 className="page-title">Dashboards</h1>
      <p className="page-subtitle">The shape of the business, rather than a list of documents.</p>
      <Panel title="Dashboards" aside={`${(defs.data ?? []).length}`}>
        <div className="tile-grid">
          {(defs.data ?? []).map((d) => (
            <div className="tile" key={d.key}>
              <StarToggle menuKey={`dashboard:${d.key}`} label={d.label} />
              <div className="tile__body">
                <Link to={paths.dashboard(companyId, d.route)} className="tile__title">{d.label}</Link>
                <p className="tile__desc">{d.blurb}</p>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  )
}

/**
 * Where a widget row leads (docs/03 — `Show details`).
 *
 * Each drillable widget names the filter its rows set, and the row carries the
 * id to set it to. A project bar opens that project's financials; a supplier
 * bar opens what has been bought from them.
 */
const REPORT_FOR: Readonly<Record<string, string>> = {
  projectId: 'project_management_dashboard',
  supplierId: 'purchase_price_history',
  jobTypeId: 'project_management_dashboard',
}

const LIGHT_WORD: Readonly<Record<Rag, string>> = { GREEN: 'on track', AMBER: 'watch', RED: 'at risk', NONE: 'not measured' }

/** A light names itself and its state in words, never colour alone. */
const Light = ({ name, rag, extra }: { name: string; rag: Rag; extra?: string }) => (
  <span className={`light light--${rag}`} title={`${name}: ${LIGHT_WORD[rag]}`} aria-label={`${name}: ${LIGHT_WORD[rag]}`}>
    <span className="light__dot" aria-hidden />{name}{extra ? <span className="pct-chip">{extra}</span> : null}
  </span>
)

const pctChip = (pct: string, signed = false): JSX.Element => {
  const n = Number(pct)
  const tone = signed ? (n < 0 ? ' pct-chip--bad' : n > 0 ? ' pct-chip--good' : '') : ''
  return <span className={`pct-chip${tone}`}>{`${n.toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`}</span>
}

const shortDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Every ongoing project, by stage, a line each (docs/03 §2). */
const HealthList = ({ groups, companyId }: {
  groups: ReadonlyArray<{ stage: string; rows: readonly HealthRow[] }>; companyId: string
}) => {
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set())
  const toggle = (stage: string): void => {
    const next = new Set(closed)
    if (next.has(stage)) next.delete(stage); else next.add(stage)
    setClosed(next)
  }
  return (
    <div>
      {groups.map((g) => (
        <section className="health-stage" key={g.stage}>
          <button type="button" className="health-stage__head" aria-expanded={!closed.has(g.stage)} onClick={() => toggle(g.stage)}>
            <span style={{ flex: 1 }}>{g.stage}</span>
            <span className="health-stage__count">{g.rows.length}</span>
            <Icon name={closed.has(g.stage) ? 'down' : 'up'} size={16} />
          </button>
          {closed.has(g.stage) ? null : g.rows.map((r) => (
            <div className="health-row" key={r.id}>
              <div>
                <div className="health-row__name">{`${r.code} - ${r.name}`}</div>
                <div className="health-row__pm">
                  {r.manager ?? 'No project manager'}
                  {r.subProjects > 0 ? ` · with ${r.subProjects} sub-project${r.subProjects === 1 ? '' : 's'}` : ''}
                  {' · '}
                  <Link to={paths.dashboard(companyId, 'project_performance_dashboard', { projectId: r.id })}>Show details</Link>
                </div>
              </div>
              {/* The three lights stacked in a column of their own, as the reference lays the row out. */}
              <div className="health-row__lights" style={{ flexDirection: 'column', marginTop: 0 }}>
                <Light name="Schedule" rag={r.schedule} extra={`${Math.round(Number(r.schedulePct ?? 0))}%`} />
                <Light name="Cost" rag={r.cost} />
                <Light name="Revenue" rag={r.revenue} />
              </div>
              <div>
                <div className="health-cell__label">Planned finish date</div>
                <div className="health-cell__value">{shortDate(r.plannedFinish)}</div>
                <div className="health-cell__label" style={{ marginTop: 8 }}>Forecast finish date</div>
                <div className="health-cell__value">{shortDate(r.forecastFinish)}</div>
              </div>
              <div>
                <div className="health-cell__label">Revenue to be recognised</div>
                <div className="health-cell__value"><Money value={r.revenueToRecognise} />{pctChip(r.revenuePct)}</div>
                <div className="health-cell__label" style={{ marginTop: 8 }}>Cost incurred</div>
                <div className="health-cell__value"><Money value={r.costIncurred} />{pctChip(r.costPct)}</div>
              </div>
              <div>
                <div className="health-cell__label">Profit at completion</div>
                <div className="health-cell__value"><Money value={r.profitAtCompletion} />{pctChip(r.profitPct, true)}</div>
                <div className="health-cell__label" style={{ marginTop: 8 }}>Cost variance at completion</div>
                <div className="health-cell__value"><Money value={r.costVarianceAtCompletion} />{pctChip(r.costVariancePct, true)}</div>
              </div>
              <div>
                <div className="health-cell__label">Task completed</div>
                <div className="health-cell__value num" title={r.tasksTotal === 0 ? 'No schedule' : `${r.tasksDone} of ${r.tasksTotal}`}>{r.tasksDone}</div>
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  )
}

/**
 * A series with its headline totals over it. Where the widget says so, the
 * totals pick what the chart shows — `Total order` every series stacked,
 * `Purchase order` that one alone (docs/03 §4).
 */
const SeriesWidget = ({ def, data }: { def: WidgetDef; data: Extract<WidgetData, { kind: 'series' }> }) => {
  const [picked, setPicked] = useState<string | null>(null)
  const coloured = data.series.map((s, i) => ({ ...s, colour: colourAt(i) }))
  const shown = picked ? coloured.filter((s) => s.name === picked) : coloured
  const chart: ChartType = def.chart === 'donut' || def.chart === undefined ? 'bar' : def.chart
  const swatch = (series?: string): string | undefined =>
    series ? coloured.find((s) => s.name === series)?.colour : undefined
  return (
    <>
      {data.totals && data.totals.length > 0 ? (
        <div className="dash-totals">
          {data.totals.map((t) => {
            const on = Boolean(def.selectable) && picked === (t.series ?? null)
            const body = (
              <>
                <div className="total-card__value">{formatFigure(t.value, def.format)}</div>
                <div className="total-card__label">{t.label}</div>
              </>
            )
            const colour = swatch(t.series)
            const style = colour ? ({ '--swatch': colour } as CSSProperties) : undefined
            return def.selectable ? (
              <button type="button" key={t.label} className={`total-card${on ? ' total-card--on' : ''}`} style={style}
                aria-pressed={on} onClick={() => setPicked(t.series ?? null)}>{body}</button>
            ) : (
              <div key={t.label} className="total-card" style={style}>{body}</div>
            )
          })}
        </div>
      ) : null}
      {shown.length > 1 ? (
        <div className="chart-legend">
          {shown.map((s) => (
            <span key={s.name} className="legend"><span className="legend__swatch" style={{ background: s.colour }} />{s.name}</span>
          ))}
        </div>
      ) : null}
      <SeriesChart labels={data.labels} series={shown} chart={chart} format={def.format} />
    </>
  )
}

const Note = ({ title, children }: { title?: string; children: ReactNode }) => (
  <div className="kpi-note" style={{ marginTop: 0 }}>
    <Icon name="info" size={16} />
    <div>
      {title ? <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{title}</div> : null}
      {children}
    </div>
  </div>
)

const Widget = ({
  def, data, companyId, filters,
}: {
  def: WidgetDef
  data: WidgetData | undefined
  companyId: string
  filters: Readonly<Record<string, string>>
}) => {
  /*
    A figure in the unit the widget declares. Every split, rank and gauge used
    to print rupees, so a count of open orders read "₹ 4.00" and days late
    read as money.
  */
  const fig = (value: string, size?: number, format: WidgetDef['format'] = def.format): JSX.Element => {
    // A figure that is not a number — a date on the timeline — is shown as it is.
    if (value.trim() === '' || !Number.isFinite(Number(value))) return <>{value}</>
    switch (format) {
      case 'money':
      case undefined:
        return <Money value={value} {...(size ? { size } : {})} />
      case 'pct':
        return <>{Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%</>
      case 'qty':
        return <>{formatQuantity(value)}</>
      default:
        return <>{Number(value).toLocaleString('en-IN', { maximumFractionDigits: 1 })}</>
    }
  }

  const openRow = (id: string | undefined): void => {
    if (!id || !def.drillTo) return
    const report = def.drillReport ?? REPORT_FOR[def.drillTo]
    if (!report) return
    // Carry the dashboard's own period across, so the report opens on the same
    // window the figure was read from.
    navigate(paths.report(companyId, report, {
      ...(filters['from'] ? { from: filters['from'] } : {}),
      ...(filters['to'] ? { to: filters['to'] } : {}),
      [def.drillTo]: id,
    }))
  }

  const body = (): ReactNode => {
    if (!data || data.kind === 'empty') {
      return <Note {...(data?.title ? { title: data.title } : {})}>{data?.note ?? 'Nothing to show for these filters.'}</Note>
    }
    switch (data.kind) {
      case 'kpi':
        return (
          <>
            <div style={{ display: 'flex', gap: 44, flexWrap: 'wrap' }}>
              {data.items.map((i) => (
                <div key={i.label}>
                  <div className="widget__label">{i.label}</div>
                  <div className="widget__figure num">{fig(i.value, 27)}</div>
                  {i.note ? <div className="total-card__label">{i.note}</div> : null}
                  {i.previous !== undefined ? (
                    // The period before, and the change on it (RPT-040).
                    <div className="widget__label">
                      from {fig(i.previous, 13)}
                      {i.changePct !== undefined ? (
                        <span style={{ color: Number(i.changePct) < 0 ? 'var(--bad)' : 'var(--good)', marginLeft: 6 }}>
                          {Number(i.changePct) < 0 ? '↘' : '↗'} {Math.abs(Number(i.changePct)).toFixed(2)}% on the previous 30 days
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            {data.note ? <div className="kpi-note"><Icon name="info" size={16} />{data.note}</div> : null}
          </>
        )
      case 'split': {
        const list = (
          <div className="splitlist" style={{ flex: 1, minWidth: 220 }}>
            {data.parts.map((p, i) => (
              <div className="splitlist__row" key={p.label}>
                <span className="splitlist__swatch num" style={{ background: colourAt(i) }}>{i + 1}</span>
                {def.drillTo && p.id ? (
                  <button type="button" className="splitlist__name linkish" onClick={() => openRow(p.id)}>{p.label}</button>
                ) : (
                  <span className="splitlist__name">{p.label}</span>
                )}
                <span className="num splitlist__pct">{p.pct}%</span>
                <span className="num splitlist__val">{fig(p.value)}</span>
              </div>
            ))}
          </div>
        )
        if (def.chart === 'donut' && data.keepZero) {
          // Counters, two by two, beside the ring — kept at 0 with the ring
          // saying why when there is nothing to count (Task Status).
          return (
            <div className="split-ring">
              <SplitRing parts={data.parts.map((p) => ({ label: p.label, value: Number(p.value) }))}
                total={data.emptyNote ? '' : formatFigure(data.total, def.format)}
                totalLabel={data.emptyNote ?? data.totalLabel ?? 'Total'} />
              <div className="status-counters">
                {[{ label: data.totalLabel ?? 'Total', value: data.total }, ...data.parts].map((p, i) => (
                  <div key={p.label}>
                    <div className="widget__figure num">{fig(p.value)}</div>
                    <div className="widget__label">
                      {i > 0 ? <span className="legend__swatch" style={{ background: colourAt(i - 1), marginRight: 6 }} /> : null}
                      {p.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        }
        if (def.chart === 'donut') {
          return (
            <div className="split-ring">
              <SplitRing parts={data.parts.map((p) => ({ label: p.label, value: Number(p.value) }))}
                total={formatFigure(data.total, def.format)} totalLabel={data.totalLabel ?? 'Total'} />
              {list}
            </div>
          )
        }
        return (
          <>
            <div className="widget__label">{data.totalLabel ?? 'Total'}</div>
            <div className="widget__figure num" style={{ marginBottom: 18 }}>{fig(data.total, 27)}</div>
            <div className="splitbar">
              {data.parts.map((p, i) => (
                <span key={p.label} title={`${p.label} ${p.pct}%`}
                  style={{ width: `${Math.max(0, Number(p.pct))}%`, background: colourAt(i) }} />
              ))}
            </div>
            {list}
          </>
        )
      }
      case 'rank': {
        const max = Math.max(...data.items.map((i) => Math.abs(Number(i.value))), 1)
        return (
          <div>
            {data.valueLabel ? (
              <div className="rankrow__head" style={{ marginBottom: 0, justifyContent: 'flex-end' }}>
                <span className="widget__label">{data.valueLabel}</span>
              </div>
            ) : null}
            {data.items.length === 0 ? <p className="muted">Nothing to show for these filters.</p> : null}
            {data.items.map((i) => (
              <div key={i.id ?? i.label} className="rankrow">
                <div className="rankrow__head">
                  {def.drillTo && i.id ? (
                    <button type="button" className="rankrow__name linkish" onClick={() => openRow(i.id)}>{i.label}</button>
                  ) : (
                    <span className="rankrow__name">{i.label}</span>
                  )}
                  <span className="num">{fig(i.value)}</span>
                </div>
                <div className="rankrow__track">
                  <span style={{ width: `${(Math.abs(Number(i.value)) / max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )
      }
      case 'gauge':
        return (
          <div>
            {data.items.map((i) => (
              <div key={i.label} className="rankrow">
                <div className="rankrow__head">
                  <span className="rankrow__name">{i.label}</span>
                  <span className="num">{fig(i.value)} · {i.pct}%</span>
                </div>
                <div className="rankrow__track">
                  <span style={{ width: `${Math.min(Number(i.pct), 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )
      case 'donut':
        return (
          <div className="rings">
            {data.rings.map((r, i) => <Ring key={r.label} pct={Number(r.pct)} label={r.label} note={r.note} colour={colourAt(i)} />)}
          </div>
        )
      case 'ratio':
        return (
          <>
            <div className="ratio-items">
              {data.items.map((i, n) => (
                <div className="ratio-item" key={i.label}>
                  <span className="ratio-item__swatch" style={{ background: colourAt(n) }} aria-hidden />
                  <span className="ratio-item__label">{i.label}</span>
                  <span className="ratio-item__value num">{fig(i.value, undefined, i.format ?? def.format)}</span>
                  {i.pct !== undefined ? (
                    <span className="ratio-item__pct num">{`${Number(i.pct).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`}</span>
                  ) : null}
                </div>
              ))}
            </div>
            <RatioBand ratio={data.ratio === null ? null : Number(data.ratio)} />
            {data.callout ? (
              <div className={`callout${Number(data.callout.value) < 0 ? ' callout--bad' : ''}`}>
                <Money value={data.callout.value} />
                <span>{`• ${Number(data.callout.pct).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`}</span>
                <span>{data.callout.label}</span>
              </div>
            ) : null}
          </>
        )
      case 'timeline':
        return (
          <>
            <div className="timeline-cells">
              {data.cells.map((c) => (
                <div className="timeline-cell" key={c.label}>
                  <div className="widget__label">{c.label}</div>
                  <div className="widget__figure num" style={{ fontSize: 18 }}>
                    {/^\d{4}-\d{2}-\d{2}$/.test(c.value) ? shortDate(c.value) : c.value}
                  </div>
                </div>
              ))}
            </div>
            <div className="timeline-legend">
              {data.legend.map((l, i) => (
                <span className="legend" key={l.label}>
                  <span className="legend__swatch" style={{ background: colourAt(i) }} />
                  {`${l.days} ${l.label}`}
                </span>
              ))}
            </div>
          </>
        )
      case 'health':
        return <HealthList groups={data.groups} companyId={companyId} />
      case 'series':
        return <SeriesWidget def={def} data={data} />
      default:
        return null
    }
  }

  const aside = [def.subtitle, def.current ? 'As of today' : undefined].filter(Boolean).join(' · ')
  return (
    <div style={{ gridColumn: def.span === 2 ? '1 / -1' : undefined }}>
      <Panel title={def.title} {...(aside ? { aside } : {})}>{body()}</Panel>
    </div>
  )
}

/* ------------------------------------------------------------- filters */

const longDate = (iso: string | undefined): string => (iso ? shortDate(iso) : '…')

/** "Period: 1 Apr 2025 to 31 Mar 2026 | Company: All", for what is applied. */
export const appliedSummary = (
  filters: readonly DashboardFilterDef[],
  values: Readonly<Record<string, string>>,
  companies: ReadonlyArray<{ id: string; name: string }>,
  labels: Readonly<Record<string, string>>,
): string => filters.flatMap((f) => {
  if (f.autoApply) return []
  if (f.control === 'dateRange') return [`${f.label}: ${longDate(values['from'])} to ${longDate(values['to'])}`]
  if (f.control === 'companies') {
    const ids = (values[f.key] ?? '').split(',').filter(Boolean)
    if (ids.length === 0) return []
    const all = companies.length > 0 && companies.every((c) => ids.includes(c.id))
    return [`${f.label}: ${all ? 'All' : ids.map((id) => companies.find((c) => c.id === id)?.name ?? 'Selected').join(', ')}`]
  }
  if (f.control === 'checkbox') return values[f.key] === 'true' ? [f.label] : []
  const v = values[f.key]
  return v ? [`${f.label}: ${labels[f.key] || 'Selected'}`] : []
}).join(' | ')

/** The group view's company choice: the companies this person belongs to. */
const CompanyPick = ({
  value, companies, onChange,
}: { value: string; companies: ReadonlyArray<{ id: string; name: string }>; onChange: (v: string) => void }) => {
  const ids = value.split(',').filter(Boolean)
  const all = companies.length > 0 && companies.every((c) => ids.includes(c.id))
  return (
    <div className="company-pick" role="group" aria-label="Companies">
      {companies.length > 1 ? (
        <label>
          <input type="checkbox" checked={all} onChange={() => onChange(all ? '' : companies.map((c) => c.id).join(','))} />
          All companies
        </label>
      ) : null}
      {companies.map((c) => (
        <label key={c.id}>
          <input type="checkbox" checked={ids.includes(c.id)}
            onChange={() => onChange((ids.includes(c.id) ? ids.filter((x) => x !== c.id) : [...ids, c.id]).join(','))} />
          {c.name}
        </label>
      ))}
    </div>
  )
}

export const DashboardPage = ({ companyId, dashboardRoute, query }: {
  companyId: string; dashboardRoute: string; query: Query
}) => {
  const api = useApi()
  const defs = useDashboardDefs()
  const def: DashboardDef | undefined = defs.data?.find((d) => d.route === dashboardRoute)
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api.companies(), staleTime: 10 * 60_000 })

  const drawerFilters = (def?.filters ?? []).filter((f) => !f.autoApply)
  const inlineFilters = (def?.filters ?? []).filter((f) => f.autoApply)

  /*
    The reference opens a dashboard with its filters filled in and waiting —
    "Apply filters! Fill the filter values and click 'Apply'". The period
    starts on the rolling year to today and the company on this one; nothing runs
    until Apply, except on a dashboard whose filters all apply themselves
    (the project picker, a tick box), which loads as they change.
  */
  const seed = useMemo(() => {
    const out: Record<string, string> = defaultFilterValues(def?.filters ?? [])
    for (const f of def?.filters ?? []) if (f.control === 'companies') out[f.key] = companyId
    return out
  }, [def, companyId])

  const [draft, setDraft] = useState<Record<string, string> | null>(null)
  const [applied, setApplied] = useState<Record<string, string> | null>(
    Object.keys(query).length > 0 ? (query as Record<string, string>) : null,
  )
  const [drawer, setDrawer] = useState<boolean | null>(null)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const values: Record<string, string> = draft ?? { ...seed, ...(query as Record<string, string>) }
  const set = (key: string, v: string): void => setDraft({ ...values, [key]: v })

  const waitsForApply = drawerFilters.length > 0
  const effective = applied ?? (waitsForApply ? null : values)

  const params = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(effective ?? {})) if (v) out[k] = v
    return out
  }, [effective])

  // The company list may be left out of a link: the server reads the current company then.
  const ready = effective !== null && def !== undefined
    && answersRequired(def.filters.filter((f) => f.control !== 'companies'), params)
  const run = useQuery<DashboardResult>({
    queryKey: ['dashboard', api.companyId, def?.key, params],
    queryFn: () => api.runDashboard(def?.key ?? '', params),
    enabled: Boolean(def?.key) && ready,
  })

  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No dashboard at '${dashboardRoute}'`)} /></div>

  const apply = (next: Record<string, string>): void => {
    setApplied(next)
    setDraft(next)
    setDrawer(false)
    const q: Record<string, string> = {}
    for (const [k, v] of Object.entries(next)) if (v) q[k] = v
    navigate(paths.dashboard(companyId, dashboardRoute, q))
  }
  // An inline filter applies itself, on top of whatever the drawer last applied.
  const applyInline = (key: string, v: string): void => {
    const next = { ...(applied ?? values), [key]: v }
    if (waitsForApply && applied === null) { setDraft(next); return }
    apply(next)
  }

  const drawerOpen = drawer ?? (waitsForApply && applied === null)
  const summary = applied ? appliedSummary(def.filters, applied, companies.data ?? [], labels) : ''

  const control = (f: DashboardFilterDef): ReactNode => {
    if (f.control === 'dateRange') {
      return (
        <DateRangePicker label={f.label} from={values['from']} to={values['to']}
          onChange={(range) => setDraft({ ...values, ...range })} />
      )
    }
    if (f.control === 'companies') {
      return <CompanyPick value={values[f.key] ?? ''} companies={companies.data ?? []} onChange={(v) => set(f.key, v)} />
    }
    if (f.control === 'checkbox') {
      return (
        <label className="dash-check">
          <input type="checkbox" checked={values[f.key] === 'true'}
            onChange={(e) => set(f.key, e.currentTarget.checked ? 'true' : '')} />
          {f.label}
        </label>
      )
    }
    if (f.control === 'lookup' && f.lookup) {
      return (
        <Lookup entity={f.lookup.entity} value={values[f.key] ?? ''}
          {...(labels[f.key] ? { label: labels[f.key] } : {})}
          onChange={(v, option) => { set(f.key, v); setLabels({ ...labels, [f.key]: option?.label ?? '' }) }}
          placeholder="Please start typing to search" />
      )
    }
    return <input className="control" value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.currentTarget.value)} />
  }

  const projectPicker = inlineFilters.find((f) => f.control === 'lookup')
  const inlineChecks = inlineFilters.filter((f) => f.control === 'checkbox')
  const header = run.data?.project

  const content = (): ReactNode => {
    if (waitsForApply && applied === null) {
      return (
        <div className="panel" style={{ padding: 24 }}>
          <Note title="Apply filters!">Fill the filter values and click the ‘Apply’ button.</Note>
        </div>
      )
    }
    if (projectPicker && !params[projectPicker.key]) {
      return (
        <div className="panel" style={{ padding: 24 }}>
          <Note title="No Project!">Please select a project to view its details.</Note>
        </div>
      )
    }
    if (run.isLoading) return <Loading />
    if (run.error) return <ErrorBlock error={run.error} />
    return (
      <div className="widget-grid">
        {def.widgets.map((w) => (
          <Widget key={w.key} def={w} data={run.data?.widgets[w.key]} companyId={companyId} filters={params} />
        ))}
      </div>
    )
  }

  return (
    <div className="page">
      <Link to={paths.hub(companyId, 'dashboards')} className="crumb"><Icon name="left" size={15} /> Dashboards</Link>
      <h1 className="page-title">{def.label}</h1>
      <p className="page-subtitle">{def.blurb}</p>

      {projectPicker || inlineChecks.length > 0 ? (
        <div className="project-head">
          {projectPicker && projectPicker.lookup ? (
            <div className="project-head__picker">
              <Lookup entity={projectPicker.lookup.entity} value={params[projectPicker.key] ?? ''}
                {...(labels[projectPicker.key] ? { label: labels[projectPicker.key] }
                  : header ? { label: `${header.code} - ${header.name}` } : {})}
                onChange={(v, option) => {
                  setLabels({ ...labels, [projectPicker.key]: option?.label ?? '' })
                  applyInline(projectPicker.key, v)
                }}
                placeholder="Please start typing to search" />
            </div>
          ) : null}
          {header ? (
            <span className="project-head__meta">
              <span className="chip" style={{ marginRight: 10 }}>{header.stage ?? '-'}</span>
              {`| Project Manager: ${header.manager ?? '-'}`}
            </span>
          ) : null}
          {inlineChecks.map((f) => (
            <label className="dash-check" key={f.key}>
              <input type="checkbox" checked={(applied ?? values)[f.key] === 'true'}
                onChange={(e) => applyInline(f.key, e.currentTarget.checked ? 'true' : '')} />
              {f.label}
            </label>
          ))}
        </div>
      ) : null}

      {waitsForApply ? (
        <div className="dash-bar">
          {summary ? <div className="dash-summary" aria-label="Applied filters">{summary}</div> : <div style={{ flex: 1 }} />}
          <Button kind="primary" icon={<Icon name="filter" size={16} />} aria-expanded={drawerOpen}
            onClick={() => setDrawer(!drawerOpen)}>Filters</Button>
        </div>
      ) : null}

      {waitsForApply && drawerOpen ? (
        <aside className="dash-drawer" aria-label="Filters">
          <div className="dash-drawer__head">
            <span className="dash-drawer__title">Filters</span>
            <Button kind="ghost" aria-label="Close filters" icon={<Icon name="x" size={16} />} onClick={() => setDrawer(false)} />
          </div>
          <div className="dash-drawer__body">
            {drawerFilters.map((f) => (
              <div className="field" key={f.key}>
                {f.control === 'checkbox' ? null : (
                  <label className="field__label">
                    {f.label}{f.required ? <span className="field__req">Required</span> : null}
                  </label>
                )}
                {control(f)}
              </div>
            ))}
          </div>
          <div className="dash-drawer__foot">
            <Button kind="primary" disabled={!answersRequired(drawerFilters, values)} onClick={() => apply(values)}>Apply</Button>
            <Button kind="ghost" onClick={() => setDraft({ ...seed })}>Reset</Button>
          </div>
        </aside>
      ) : null}

      <div style={{ marginTop: 8 }}>{content()}</div>
    </div>
  )
}
